import { useState, useEffect } from 'react'
import styled from 'styled-components'
import Faq from './faq'
import Tnc from './tnc'
import Enquiry from './enquiry'
import { useTranslation } from 'react-i18next'
import { adobePageViewEvent, adobeInteractionEvent } from 'utils/adobewa'

const HelpCentreContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    background-color: #e6dfd7;
`

const PageTilteText = styled.div`
    color: #121b21;
    text-align: center;
    font-size: 30px;
    font-style: normal;
    font-weight: 700;
    line-height: 40px; /* 125% */
    padding-top: 32px;
    padding-bottom: 24px;
`

const TabsContentContainer = styled.div`
    width: 358px;
    display: flex;
    flex-direction: column;
`

const TabsContainer = styled.div`
    width: 100%;
    height: 56px;
    display: flex;
    flex-direction: row;
`

interface TabItemProps {
    $selected: boolean
    $whiteColor: boolean
}

const TabItem = styled.div<TabItemProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px 8px;
    width: 100%;
    background-color: ${(p) => (p.$selected ? '#fffcf6' : '#F8EDDC')};
    border-left: ${(p) =>
        p.$selected
            ? '4px solid var(--Brand-Color-Yellow, #c5924d)'
            : p.$whiteColor
              ? '3px solid #FBF5EC'
              : ''};
    cursor: pointer;
`

interface Props {
    $selected: boolean
}

const TabLabelText = styled.div<Props>`
    color: ${(p) => (p.$selected ? '#C5924D' : '#877')};
    text-align: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 20px; /* 125% */
`

export default function HelpCentre() {
    const { t, i18n } = useTranslation('', {
        keyPrefix: 'helpcentre.helpcentre',
    })
    const [selectedTab, setSelectedTab] = useState('faq')
    const renderPageContent = () => {
        switch (selectedTab) {
            case 'faq':
                return <Faq />
            case 'tnc':
                return <Tnc />
            case 'enquiry':
                return <Enquiry />
            default:
                return <Faq />
        }
    }

    const lang = i18n.language

    const tabOnClick = (value: string) => {
        setSelectedTab(value)
        adobeInteractionEvent({
            lang: lang,
            eventID: `prioritysite_clickTab_Help_${value}`,
        })
    }

    useEffect(() => {
        adobePageViewEvent(lang)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const handleScroll = () => {
            // Calculate current scroll position and window height
            const scrollY = window.pageYOffset
            const windowHeight = window.innerHeight

            // Calculate document height
            const documentHeight = Math.max(
                document.body.scrollHeight,
                document.documentElement.scrollHeight,
                document.body.offsetHeight,
                document.documentElement.offsetHeight,
                document.documentElement.clientHeight
            )

            // Check if the user has scrolled to the bottom (or very close)
            // A small threshold can be added to account for potential rounding issues
            const threshold = 5 // Pixels from the bottom to consider "at bottom"
            if (scrollY + windowHeight >= documentHeight - threshold) {
                adobeInteractionEvent({
                    lang: lang,
                    eventID: 'prioritysite_Scroll_Page_Bottom-Link',
                })
                window.removeEventListener('scroll', handleScroll)
            }
        }

        // Add scroll event listener
        window.addEventListener('scroll', handleScroll)

        // Clean up the event listener when the component unmounts
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <HelpCentreContainer>
            <PageTilteText>{t('HelpCentre')}</PageTilteText>
            <TabsContentContainer>
                <TabsContainer>
                    <TabItem
                        $selected={selectedTab === 'faq'}
                        $whiteColor={false}
                        onClick={() => tabOnClick('faq')}
                    >
                        <TabLabelText $selected={selectedTab === 'faq'}>
                            {t('FAQ')}
                        </TabLabelText>
                    </TabItem>
                    <TabItem
                        $selected={selectedTab === 'tnc'}
                        $whiteColor={selectedTab === 'enquiry'}
                        onClick={() => tabOnClick('tnc')}
                    >
                        <TabLabelText $selected={selectedTab === 'tnc'}>
                            {t('TERMSCONDITIONS')}
                        </TabLabelText>
                    </TabItem>
                    <TabItem
                        $selected={selectedTab === 'enquiry'}
                        $whiteColor={selectedTab === 'faq'}
                        onClick={() => tabOnClick('enquiry')}
                    >
                        <TabLabelText $selected={selectedTab === 'enquiry'}>
                            {t('ENQUIRY')}
                        </TabLabelText>
                    </TabItem>
                </TabsContainer>
            </TabsContentContainer>
            {renderPageContent()}
        </HelpCentreContainer>
    )
}
